import { useEffect, useRef, useState } from "react";
import { API, graphqlOperation, Hub } from "aws-amplify";
import {
  onCreateTrackCarMini,
  onUpdateTrackCarMini,
  onDeleteTrackCarMini,
} from "../graphql/trackCar/trackCar-mutations";
import { logError } from "./appUtils";

const trackCarEvents = [
  {
    eventName: "create",
    query: onCreateTrackCarMini,
    getPayload: (value) => value?.data?.onCreateTrackCar,
  },
  {
    eventName: "update",
    query: onUpdateTrackCarMini,
    getPayload: (value) => value?.data?.onUpdateTrackCar,
  },
  {
    eventName: "delete",
    query: onDeleteTrackCarMini,
    getPayload: (value) => value?.data?.onDeleteTrackCar,
  },
];

const RESUBSCRIBE_DELAY_MS = 0;
const MAX_CONNECTION_CLOSED_RESUBSCRIBES = 1;

const getSubscriptionErrorMessage = (error) =>
  error?.errors?.[0]?.message ||
  error?.error?.errors?.[0]?.message ||
  error?.message ||
  "";

/**
 * AppSync real-time WebSockets close for benign reasons (OIDC refresh, idle,
 * network churn, OS backgrounding). Those surface as subscription observer errors;
 * they are not application bugs and should not hit user-facing error logging.
 */
const getRecoverableSubscriptionErrorKind = (error) => {
  const msg = getSubscriptionErrorMessage(error).trim().toLowerCase();

  if (msg.includes("subscription timeout")) return "subscription-timeout";
  if (msg === "connection closed") return "connection-closed";

  return null;
};

export const shouldRefreshInventoryTrackCar = ({
  trackCar,
  reloadYardId,
  inboundShipmentId,
  recordId,
}) => {
  if (!trackCar || trackCar.reloadYardId !== reloadYardId) return false;

  const affectedShipmentId = trackCar.shipmentId || trackCar.id;
  return (
    affectedShipmentId === inboundShipmentId || affectedShipmentId === recordId
  );
};

export const shouldRefreshInventoryItemTrackCar = ({
  trackCar,
  reloadYardId,
  currentInboundId,
  currentOutboundId,
  recordId,
}) => {
  if (!trackCar || trackCar.reloadYardId !== reloadYardId) return false;

  const affectedShipmentId = trackCar.shipmentId || trackCar.id;
  return (
    affectedShipmentId === currentInboundId ||
    affectedShipmentId === currentOutboundId ||
    trackCar.id === recordId ||
    trackCar.shipmentId === recordId
  );
};

export const useTrackCarSubscriptionRefresh = ({
  enabled = true,
  label,
  notify = null,
  shouldRefresh,
  onRefresh,
}) => {
  const shouldRefreshRef = useRef(shouldRefresh);
  const onRefreshRef = useRef(onRefresh);
  const resubscribeTimeoutRef = useRef(null);
  const pendingReconnectRefreshRef = useRef(false);
  const connectionClosedResubscribeCountRef = useRef(0);
  const [subscriptionEpoch, setSubscriptionEpoch] = useState(0);

  useEffect(() => {
    shouldRefreshRef.current = shouldRefresh;
    onRefreshRef.current = onRefresh;
  }, [shouldRefresh, onRefresh]);

  useEffect(() => {
    return () => {
      if (resubscribeTimeoutRef.current) {
        clearTimeout(resubscribeTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (!enabled) return undefined;
    const removeHubListener = Hub.listen("api", ({ payload }) => {
      if (payload?.event !== "connected") return;
      if (!pendingReconnectRefreshRef.current) return;

      pendingReconnectRefreshRef.current = false;
      connectionClosedResubscribeCountRef.current = 0;
      onRefreshRef.current?.(null, "reconnected");
    });
    return () => removeHubListener();
  }, [enabled]);

  useEffect(() => {
    if (!enabled) return undefined;

    const scheduleResubscribe = (recoverableErrorKind) => {
      if (resubscribeTimeoutRef.current) return;

      resubscribeTimeoutRef.current = setTimeout(() => {
        resubscribeTimeoutRef.current = null;
        if (recoverableErrorKind === "connection-closed") {
          connectionClosedResubscribeCountRef.current += 1;
        }
        setSubscriptionEpoch((currentEpoch) => currentEpoch + 1);
      }, RESUBSCRIBE_DELAY_MS);
    };

    const subscriptions = trackCarEvents.map(
      ({ eventName, query, getPayload }) =>
        API.graphql(graphqlOperation(query)).subscribe({
          next: ({ value }) => {
            connectionClosedResubscribeCountRef.current = 0;
            const trackCar = getPayload(value);
            if (shouldRefreshRef.current?.(trackCar, eventName)) {
              onRefreshRef.current?.(trackCar, eventName);
            }
          },
          error: (err) => {
            const recoverableErrorKind =
              getRecoverableSubscriptionErrorKind(err);
            const canRetryConnectionClosed =
              recoverableErrorKind === "connection-closed" &&
              connectionClosedResubscribeCountRef.current <
                MAX_CONNECTION_CLOSED_RESUBSCRIBES;

            if (
              recoverableErrorKind === "subscription-timeout" ||
              canRetryConnectionClosed
            ) {
              pendingReconnectRefreshRef.current = true;
              scheduleResubscribe(recoverableErrorKind);
              return;
            }

            pendingReconnectRefreshRef.current = false;
            logError(`${label}:${eventName}`, err, notify);
          },
        }),
    );

    return () => {
      subscriptions.forEach((subscription) => {
        if (typeof subscription?.unsubscribe === "function") {
          subscription.unsubscribe();
        }
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, label, subscriptionEpoch]);
};
